// /var/www/oszczedzam/client/src/components/reports/DataExport.js
import React, { useState } from 'react';
import { 
  Box, Paper, Typography, Grid, Button, FormControlLabel, 
  Checkbox, Card, CardContent, Divider, Alert, Snackbar,
  List, ListItem, ListItemIcon, ListItemText
} from '@mui/material';
import { 
  FileDownload as FileDownloadIcon,
  TableChart as TableChartIcon,
  PictureAsPdf as PdfIcon,
  Security as SecurityIcon,
  Check as CheckIcon
} from '@mui/icons-material';

function DataExport({ transactions, filters }) {
  const [includePersonalInfo, setIncludePersonalInfo] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', type: 'success' });

  // Przygotuj dane do eksportu CSV
  const prepareCSVData = () => {
    if (!transactions || transactions.length === 0) {
      return '';
    }
    
    // Określ, które pola mają być eksportowane
    const fields = [
      'title', 'amount', 'category', 'date', 
      'description', 'isRecurring', 'tags'
    ];
    
    // Nagłówek CSV
    let csv = fields.join(',') + '\n';
    
    // Dane
    transactions.forEach(transaction => {
      const row = fields.map(field => {
        if (field === 'amount') {
          return transaction[field];
        } else if (field === 'isRecurring') {
          return transaction[field] ? 'true' : 'false';
        } else if (field === 'tags') {
          // Połącz tagi i zadbaj o prawidłowe formatowanie
          return transaction[field] && transaction[field].length 
            ? '"' + transaction[field].join(';') + '"' 
            : '';
        } else {
          // Zadbaj o prawidłowe formatowanie pól tekstowych
          const value = transaction[field] || '';
          return typeof value === 'string' && value.includes(',') 
            ? '"' + value.replace(/"/g, '""') + '"' 
            : value;
        }
      });
      
      csv += row.join(',') + '\n';
    });
    
    return csv;
  };
  
  // Przygotuj dane do eksportu PDF
  const preparePDFContent = () => {
    // Ta funkcja w rzeczywistej implementacji przygotowywałaby
    // dane do wygenerowania PDF w odpowiednim formacie
    // Na potrzeby tego przykładu zwraca prosty obiekt z danymi
    return {
      transactions,
      filters,
      includePersonalInfo,
      date: new Date().toISOString()
    };
  };

  // Eksportuj dane do CSV
  const exportToCSV = () => {
    try {
      const csvData = prepareCSVData();
      
      if (!csvData) {
        showNotification('Brak danych do eksportu', 'error');
        return;
      }
      
      // Utwórz nazwę pliku
      const date = new Date();
      const fileName = `transakcje_${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}.csv`;
      
      // Utwórz blob i link do pobrania
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showNotification('Eksport do CSV zakończony pomyślnie', 'success');
    } catch (error) {
      console.error('Błąd podczas eksportu do CSV:', error);
      showNotification('Wystąpił błąd podczas eksportu do CSV', 'error');
    }
  };

  // Eksportuj dane do PDF
  const exportToPDF = () => {
    try {
      const pdfData = preparePDFContent();
      
      if (!pdfData || !pdfData.transactions || pdfData.transactions.length === 0) {
        showNotification('Brak danych do eksportu', 'error');
        return;
      }
      
      // W rzeczywistej implementacji należałoby tu użyć biblioteki do generowania PDF
      // np. jsPDF, pdfmake, itp.
      // Dla uproszczenia tego przykładu, nie implementujemy faktycznego generowania PDF
      
      showNotification('Eksport do PDF zakończony pomyślnie', 'success');
    } catch (error) {
      console.error('Błąd podczas eksportu do PDF:', error);
      showNotification('Wystąpił błąd podczas eksportu do PDF', 'error');
    }
  };

  // Wyświetl powiadomienie
  const showNotification = (message, type) => {
    setNotification({
      open: true,
      message,
      type
    });
  };

  // Zamknij powiadomienie
  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  // Formatuj kwotę
  const formatAmount = (amount) => {
    return amount.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' });
  };
  
  // Generuj podsumowanie eksportowanych danych
  const generateExportSummary = () => {
    if (!transactions || transactions.length === 0) return null;
    
    const summary = {
      transactionCount: transactions.length,
      dateRange: getDateRange(),
      incomeTotal: 0,
      expenseTotal: 0,
      categories: new Set()
    };
    
    transactions.forEach(transaction => {
      if (transaction.amount > 0) {
        summary.incomeTotal += transaction.amount;
      } else {
        summary.expenseTotal += Math.abs(transaction.amount);
      }
      
      if (transaction.category) {
        summary.categories.add(transaction.category);
      }
    });
    
    return summary;
  };
  
  // Pobierz zakres dat
  const getDateRange = () => {
    if (!transactions || transactions.length === 0) return null;
    
    const dates = transactions.map(t => new Date(t.date));
    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));
    
    const formatDate = (date) => {
      return date.toLocaleDateString('pl-PL', { year: 'numeric', month: 'short', day: 'numeric' });
    };
    
    return {
      from: formatDate(minDate),
      to: formatDate(maxDate)
    };
  };

  // Podsumowanie eksportowanych danych
  const exportSummary = generateExportSummary();

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>Eksport danych</Typography>
        
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <TableChartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Eksport do CSV
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Eksportuj dane w formacie CSV (Comma Separated Values), który można otworzyć w Excel, Google Sheets lub innych programach do arkuszy kalkulacyjnych.
                </Typography>
                <Button 
                  variant="contained" 
                  startIcon={<FileDownloadIcon />}
                  onClick={exportToCSV}
                  disabled={!transactions || transactions.length === 0}
                  fullWidth
                >
                  Eksportuj do CSV
                </Button>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <PdfIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Eksport do PDF
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Eksportuj dane w formacie PDF, który zachowa formatowanie i będzie łatwy do wydrukowania lub udostępnienia.
                </Typography>
                <Button 
                  variant="contained" 
                  startIcon={<FileDownloadIcon />}
                  onClick={exportToPDF}
                  disabled={!transactions || transactions.length === 0}
                  fullWidth
                >
                  Eksportuj do PDF
                </Button>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Opcje prywatności
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Anonimizacja danych przed eksportem zwiększa bezpieczeństwo i prywatność Twoich informacji finansowych.
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={includePersonalInfo}
                      onChange={(e) => setIncludePersonalInfo(e.target.checked)}
                    />
                  }
                  label="Uwzględnij informacje osobiste"
                />
              </CardContent>
            </Card>
          </Grid>
          
          {/* Podsumowanie danych do eksportu */}
          {exportSummary && (
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Podsumowanie eksportowanych danych
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        Liczba transakcji
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {exportSummary.transactionCount}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        Zakres dat
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {exportSummary.dateRange.from} - {exportSummary.dateRange.to}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        Suma przychodów
                      </Typography>
                      <Typography variant="body1" fontWeight="medium" color="success.main">
                        {formatAmount(exportSummary.incomeTotal)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        Suma wydatków
                      </Typography>
                      <Typography variant="body1" fontWeight="medium" color="error.main">
                        {formatAmount(exportSummary.expenseTotal)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}
          
          {/* Wskazówki dotyczące eksportu */}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Wskazówki dotyczące eksportu danych
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Format CSV jest idealny do analizy danych w arkuszach kalkulacyjnych."
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Format PDF jest odpowiedni do drukowania lub archiwizacji."
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Możesz użyć filtrów, aby wyeksportować tylko interesujące Cię dane."
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Anonimizacja danych przed eksportem zwiększa bezpieczeństwo Twoich informacji finansowych."
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Powiadomienia */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.type} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default DataExport;